import { isEmpty } from 'lodash';
import { TariffPlan } from '@app/types/TariffPlan';
import { Subscription } from '@app/hooks/query/useSubscriptionData.js';
import { SubscriptionStatus, SubscriptionBillingPeriod } from '@app/config';
import { isBusinessUserAccount } from '@app/utils/userUtils.js';
import { ModalStep } from '../types';
import { TagColor } from '@app/common/components/tag/Tag';

export const canUpdateToPlan = (plan: TariffPlan, currentPlan: Subscription, accountType: string): boolean => {
    if (isEmpty(currentPlan)) {
        return true;
    }

    if (isBusinessUserAccount(accountType)) {
        if (currentPlan.status === SubscriptionStatus.TRIALING) {
            return true;
        }
        return currentPlan.counts.documents <= plan.agreementsPerMonth.freeDocs;
    } else {
        return currentPlan.counts.agreements <= plan.agreementsPerMonth.max;
    }
};

export const formatPriceDisplay = (price: number): string => {
    return `$${price}`;
};

export const getBillingText = (billingFrequency: SubscriptionBillingPeriod): string => {
    return billingFrequency === SubscriptionBillingPeriod.MONTHLY ? 'Billed Monthly' : 'Billed Yearly';
};

export const getModalTitle = (step: ModalStep): string => {
    return step === ModalStep.SELECT ? 'Change Plan' : 'Confirm Plan Changes';
};

/**
 * Gets the appropriate tag color for a subscription status
 */
export const getPlanStatusTagColor = (status: SubscriptionStatus): TagColor => {
    switch (status) {
        case SubscriptionStatus.ACTIVE:
            return TagColor.Green;
        case SubscriptionStatus.CANCELLING:
        case SubscriptionStatus.TRIALING:
        case SubscriptionStatus.PAUSED:
            return TagColor.Yellow;
        case SubscriptionStatus.CANCELED:
        case SubscriptionStatus.PAST_DUE:
        case SubscriptionStatus.INACTIVE:
        case SubscriptionStatus.UNPAID:
        case SubscriptionStatus.INCOMPLETE_EXPIRED:
        case SubscriptionStatus.INCOMPLETE:
            return TagColor.Red;
        default:
            return TagColor.Grey;
    }
};

/**
 * Gets the display text for a subscription status
 */
export const getPlanStatusTagText = (status: SubscriptionStatus): string => {
    switch (status) {
        case SubscriptionStatus.ACTIVE:
            return 'Active';
        case SubscriptionStatus.CANCELLING:
            return 'Cancelling';
        case SubscriptionStatus.TRIALING:
            return 'Free Trial';
        case SubscriptionStatus.CANCELED:
            return 'Cancelled';
        case SubscriptionStatus.PAST_DUE:
            return 'Past Due';
        case SubscriptionStatus.INACTIVE:
            return 'Inactive';
        case SubscriptionStatus.UNPAID:
            return 'Unpaid';
        case SubscriptionStatus.PAUSED:
            return 'Paused';
        case SubscriptionStatus.INCOMPLETE_EXPIRED:
        case SubscriptionStatus.INCOMPLETE:
            return 'Incomplete';
        default:
            return 'Unknown';
    }
};

/**
 * Gets the display text for billing period
 */
export const getBillingPeriodText = (billingPeriod: string): string => {
    switch (billingPeriod) {
        case SubscriptionBillingPeriod.MONTHLY:
            return 'Monthly';
        case SubscriptionBillingPeriod.YEARLY:
            return 'Yearly';
        default:
            throw new Error(`Unknown billing period: ${billingPeriod}`);
    }
};

/**
 * Gets the appropriate text for the change subscription details button
 */
export const getChangeSubscriptionDetailsText = (subscription?: Subscription): string | null => {
    if (
        !subscription ||
        !subscription.status ||
        [SubscriptionStatus.PAST_DUE, SubscriptionStatus.UNPAID].includes(subscription.status)
    ) {
        return null;
    }

    if (subscription.status === SubscriptionStatus.PAUSED) {
        return 'Resume subscription';
    }

    if (subscription.active) {
        if (subscription.status === SubscriptionStatus.CANCELLING) {
            return 'Reactivate subscription';
        }
        return 'Change Plan';
    } else {
        return 'Select Plan';
    }
};
